import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'buyer' | 'seller' | 'admin';
  allowedRoles?: ('buyer' | 'seller' | 'admin')[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRole, 
  allowedRoles 
}) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Only log when there are issues or for debugging edit routes
  if (location.pathname.includes('/edit-furniture/') || !user || (requiredRole && user.role !== requiredRole)) {
    console.log('ProtectedRoute - path:', location.pathname, 'user:', user?.email, 'role:', user?.role, 'loading:', loading, 'requiredRole:', requiredRole);
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    console.log('ProtectedRoute - No user found, redirecting to login');
    // Redirect to login with the attempted location
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role-based access
  if (requiredRole && user.role !== requiredRole) {
    console.log('ProtectedRoute - Role mismatch. User role:', user.role, 'Required role:', requiredRole);
    // Redirect to appropriate dashboard based on user's role
    const redirectPath = user.role === 'seller' ? '/seller/dashboard' : '/buyer/dashboard';
    return <Navigate to={redirectPath} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role as 'buyer' | 'seller' | 'admin')) {
    // Redirect to appropriate dashboard based on user's role
    const redirectPath = user.role === 'seller' ? '/seller/dashboard' : '/buyer/dashboard';
    return <Navigate to={redirectPath} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;